import { MonetizationType } from '@/@types/graphql-types';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { Browser } from '@/helpers/composables/useUserAgent';
import { useExperiment } from '@/libraries/experiment';
import { getExperimentAffiliateTag } from '@/libraries/experiment/experiment-affiliation';
import { computed } from 'vue';

export interface PPSwiperRule {
	packages: { id: string; shortName?: string; offerCountry?: string; placement: 'ft' | 'sqtv' }[];
	slideDelay: number;
	rules: {
		selectors: {
			browsers: Browser[];
			geoLocations: string[];
		};
		id: string;
	}[];
	default: {
		id: string;
	};
}

export function useLowPerfExp() {
	const { activeVariantWithControlGroup: lowPerfExpVariant, experimentInfo } =
		useExperiment('EXP_LOWPERF_URL_SWIPER_2');

	const variantPlacementMap = {
		variant_1: { placements: ['ft', 'ft'], packages: ['atp', 'cru'] },
		variant_2: { placements: ['ft', 'sqtv'], packages: ['atp', 'jwt'] },
	} as const;

	const lowPerfExpSQTVFilter = {
		monetizationTypes: [MonetizationType.Free],
		imdbVotes: {
			min: 1000,
		},
		imdbScore: {
			min: 7,
		},
	};

	const lowPerfSwiperConfig = computed<PPSwiperRule | null>(() => {
		if (!lowPerfExpVariant.value || lowPerfExpVariant.value === 'control') return null;

		const mapping = variantPlacementMap[lowPerfExpVariant.value];

		return {
			packages: [
				{ id: 'atp', shortName: mapping.packages[0], placement: mapping.placements[0] },
				{ id: 'alt_provider', shortName: mapping.packages[1], placement: mapping.placements[1] },
			],
			slideDelay: 1500,
			rules: [],
			default: { id: 'alt_provider' },
		};
	});

	function setLowPerfTags(url: ClickoutUrl, placement: 'sqtv' | 'ft', swiperIndex: number | undefined) {
		if (!lowPerfExpVariant.value) return url;

		const affiliationShortId = experimentInfo.value.affiliationShortId as string;

		const baseTag = getExperimentAffiliateTag({
			affiliationShortId,
			variant: lowPerfExpVariant.value,
			country: 'US',
			placement,
			providerShortName: null,
		});

		const swiperSuffix = swiperIndex !== undefined ? `-p${swiperIndex}` : '';

		const tag = baseTag + swiperSuffix;

		return url.set('uct_ctag', tag);
	}

	return {
		lowPerfSwiperConfig,
		setLowPerfTags,
		lowPerfExpSQTVFilter,
	};
}
